import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import Card from "common/Boxes/Card";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import { getPurchaseSubscriptionPath } from "features/settings/common/utils";
import React, { CSSProperties, forwardRef, ReactNode, useContext } from "react";
import { SMALL_MOBILE } from "common/constants";
import { typography } from "@shared/common/Themes/typography";
import { DiscountTag } from "features/settings/Subscription/styles";
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import DowngradeQuotaModal from "features/settings/Subscription/DowngradeQuotaModal";
import { useHistory } from "react-router-dom";
import CancelSubscriptionModal from "features/settings/Billing/CancelSubscriptionModal";
import usePlanCard from "features/settings/Subscription/usePlanCard.ts";
import { SUBSCRIPTION_STEPS } from "@shared/features/settings/subscription/types";
import { FormattedPlanDetails, SubscriptionDuration } from "@shared/types";
import ConfirmDowngradeModal from "features/settings/Subscription/ConfirmDowngradeModal";
import AutoRenewalModal from "features/settings/Subscription/AutoRenewalModal";
import { getQuotaPlanFeature } from "features/settings/Subscription/utils";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface PlanCardProps {
  plan: FormattedPlanDetails;
  isUpgrade?: boolean;
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
  style?: CSSProperties;
  description?: string;
  isEnterprise?: boolean;
  rowHasCurrentPlan?: boolean;
}

const PlanCard = forwardRef<HTMLDivElement, PlanCardProps>(
  (
    {
      plan,
      isUpgrade,
      promoCodeDiscount,
      style,
      description,
      isEnterprise,
      rowHasCurrentPlan,
    },
    ref
  ) => {
    const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
    const { subscriptionParams, promoCode } = useContext(
      BuySubscriptionContext
    )!;
    const queryDuration = subscriptionParams.duration;
    const theme = useTheme();
    const history = useHistory();

    const onUpgradeClick = () => {
      const isNextPathUpgrade = !user?.isPurchaseSubscription();
      history.push(
        getPurchaseSubscriptionPath({
          isUpgrade: isNextPathUpgrade,
          duration: isNextPathUpgrade ? undefined : queryDuration,
          isAllPlans: true,
          step: SUBSCRIPTION_STEPS.DURATION,
          plan: plan.subscription_plan_type,
          promoCode,
        })
      );
    };

    const {
      user,
      isLoading,
      isCurrentPlan,
      upgradePrice,
      isPromoCodeDiscount,
      isPopular,
      isPremium,
      yearlyAmountDiscount,
      getMonthlyPrice,
      getPurchaseButtonText,
      getPlanFeatures,
      onBuyClick,
      confirmDowngradeModalProps,
      autoRenewalModalProps,
      downgradeQuotaModalProps,
      cancelSubscriptionModalProps,
      showFreeTag,
      isErrorButton,
    } = usePlanCard({
      plan,
      isUpgrade,
      promoCodeDiscount,
      getQuotaPlanFeature,
      onUpgradeClick,
    });

    const monthlyPrice = getMonthlyPrice();
    const getPrice = () => {
      const onlyShowTotal =
        isUpgrade && (plan.upgradePlan?.days_left || 0) <= 30;
      return (
        <>
          <Flex $justifyContent="flex-start">
            <Price>
              <span>$</span>
              {onlyShowTotal ? upgradePrice : monthlyPrice}
            </Price>
            <PeriodWrapper>
              {!onlyShowTotal && <span>{t("duration_radio.per_month")}</span>}
            </PeriodWrapper>
          </Flex>
          {isUpgrade && !onlyShowTotal && (
            <Text type="secondary" $size={typography.fontSizes.L}>
              ${upgradePrice} {t("plan_card.total")}
            </Text>
          )}
        </>
      );
    };

    const getFeatureText = (feature: ReactNode, index: number) => {
      const iconStyle: CSSProperties = {
        marginRight: 8,
        top: 2,
        position: "relative",
      };
      const icon = isPopular ? (
        <CheckCircleFilled
          style={{ color: theme.colors.primaryBlue, ...iconStyle }}
        />
      ) : (
        <CheckOutlined style={iconStyle} />
      );

      return (
        <FeatureText as={"div"} key={index}>
          {icon}
          {feature}
        </FeatureText>
      );
    };

    const getPurchaseButton = () => {
      return (
        <StyledButton
          type="primary"
          size="large"
          onClick={onBuyClick}
          loading={isLoading}
          $error={isErrorButton}
        >
          {getPurchaseButtonText()}
        </StyledButton>
      );
    };

    const getCurrentPlanText = () => {
      if (!isCurrentPlan) return;
      return (
        <CurrentPlan>
          <CheckOutlined style={{ marginRight: 6 }} />
          {t("plan_card.current_plan")}
          {user?.is_trial ? ` ${t("plan_card.trial")}` : ""}
        </CurrentPlan>
      );
    };

    const tags = (
      <>
        {showFreeTag && <StyledTag>{t("plan_card.no_cost")}</StyledTag>}
        {!!yearlyAmountDiscount && (
          <StyledTag $isPromoCode={isPromoCodeDiscount}>
            {t("plan_card.you_save_yearly", { amount: yearlyAmountDiscount })}
          </StyledTag>
        )}
      </>
    );

    const getContent = () => {
      if (isEnterprise) {
        return (
          <StyledCard $isCurrentPlan={isCurrentPlan} $isPremium={isPremium}>
            <EnterpriseCardInner>
              <div>
                <Flex>
                  <Title style={{ fontSize: 22 }}>
                    {plan.subscription_type}
                  </Title>
                </Flex>
                {description && (
                  <DescriptionText>{description}</DescriptionText>
                )}
              </div>
              <EnterpriseBottomSection>
                <StartingFromLabel>Starting from</StartingFromLabel>
                {getPrice()}
                {tags}
                {getPurchaseButton()}
              </EnterpriseBottomSection>
            </EnterpriseCardInner>
          </StyledCard>
        );
      }

      return (
        <StyledCard $isCurrentPlan={isCurrentPlan} $isPremium={isPremium}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Flex>
              <Title style={{ fontSize: 22 }}>{plan.subscription_type}</Title>
            </Flex>
            {getPrice()}
            {tags}
          </div>
          {getPurchaseButton()}
          {getPlanFeatures()?.map(getFeatureText)}
        </StyledCard>
      );
    };

    return (
      <Wrapper style={style} ref={ref} $rowHasCurrentPlan={rowHasCurrentPlan}>
        {getCurrentPlanText()}
        {getContent()}
        <DowngradeQuotaModal {...downgradeQuotaModalProps} />
        <ConfirmDowngradeModal {...confirmDowngradeModalProps} />
        <AutoRenewalModal {...autoRenewalModalProps} />
        <CancelSubscriptionModal {...cancelSubscriptionModalProps} />
      </Wrapper>
    );
  }
);

export default PlanCard;

const Wrapper = styled.div<{ $rowHasCurrentPlan?: boolean }>`
  position: relative;
  height: 100%;
  padding-top: ${(p) => (p.$rowHasCurrentPlan ? "20px" : "0")};
`;
const StyledCard = styled(Card)<{
  $isCurrentPlan?: boolean;
  $isPremium?: boolean;
}>`
  width: auto;
  height: 100%;
  min-width: 0;
  ${(p) =>
    p.$isPremium
      ? `
    background: linear-gradient(334deg, #5a9afd 0.87%, #3272FE 23.21%, #03204a 83.84%) !important;
    box-shadow: none !important;
    color: #ffffff !important;
    *:not(button):not(button *) {
      color: #ffffff !important;
    }
  `
      : `
    border: ${p.theme.borders.lightest};
  `}
  ${(p) =>
    p.$isCurrentPlan &&
    `
    border: 2px solid ${p.theme.colors.primaryBlue} !important;
  `}
  position: relative;
  z-index: 1;
  padding: 30px 24px 24px;
  @media (max-width: 1500px) {
    padding: 30px 20px 20px;
  }
  @media (max-width: ${SMALL_MOBILE}) {
    padding: 28px 16px 16px;
  }
`;
const PeriodWrapper = styled.div`
  padding: 8px 0 0 8px;
  display: flex;
  flex-direction: column;

  span {
    color: ${(p) => p.theme.colors.secondaryText};
    line-height: 16px;
    font-size: ${(p) => p.theme.typography.fontSizes.S};
  }
`;
const Price = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.XXL};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const DescriptionText = styled.div`
  color: ${(p) => p.theme.colors.textColor};
  font-size: ${(p) => p.theme.typography.fontSizes.M};
  line-height: 1.6;
  padding: 8px 0 16px 0;
`;
const EnterpriseCardInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const EnterpriseBottomSection = styled.div`
  margin-top: auto;
`;
const StartingFromLabel = styled.div`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  font-size: ${(p) => p.theme.typography.fontSizes.M};
  color: ${(p) => p.theme.colors.primaryText};
  margin-top: 8px;
`;
const FeatureText = styled.div`
  display: flex;
  align-items: baseline;
  padding: 6px 0;
  color: ${(p) => p.theme.colors.textColor};
  min-width: 0;
  overflow: hidden;
`;
const StyledButton = styled(Button)`
  width: 100%;
  margin: 12px 0 20px 0;
  font-weight: 600;
  border-width: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent !important;
  color: ${(p) => p.theme.colors.primaryBlue} !important;

  &&&:hover {
    background: transparent !important;
    color: ${(p) => p.theme.colors.primaryBlue} !important;
  }
`;
const CurrentPlan = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(p) => p.theme.colors.primaryBlue};
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${(p) => p.theme.colors.buttonText};
  border-radius: 20px;
  padding: 6px 16px;
  z-index: 2;
  white-space: nowrap;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const StyledTag = styled(DiscountTag)`
  margin: 2px 0 4px;
  padding: 0;
  background: none;
  border: none;
  &&& {
    color: #70a8ff !important;
  }
`;
